// src/redux/slices/productSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/axiosInstance"; // Import axiosInstance

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (
    { latest = false, popular = false, params = {} },
    { rejectWithValue }
  ) => {
    try {
      let queryParams = new URLSearchParams(params);

      if (latest) queryParams.append("latest", "true");
      if (popular) queryParams.append("popular", "true");

      const response = await axiosInstance.get(
        `/api/website/products?${queryParams}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchLatestProducts = createAsyncThunk(
  "products/fetchLatestProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `/api/website/products/latest?${queryParams}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchPopularProducts = createAsyncThunk(
  "products/fetchPopularProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `/api/website/products/popular?${queryParams}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchProductById = createAsyncThunk(
  "products/fetchProductById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/website/products/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchProductsByGroup = createAsyncThunk(
  "products/fetchProductsByGroup",
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/website/products?group=${groupId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchSearchProducts = createAsyncThunk(
  "search/fetchSearchProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await axiosInstance.get(
        `/api/website/products/search?${queryParams}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// export const fetchRelatedProducts = createAsyncThunk(
//   "products/fetchRelatedProducts",
//   async (productId, { rejectWithValue }) => {
//     try {
//       const response = await axiosInstance.get(`/api/website/products/related/${productId}`);
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.response?.data || "Something went wrong");
//     }
//   }
// );

const productSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    latestProducts: [],
    popularProducts: [],
    bestSellerProducts: [],
    product: {},
    productsByGroup: {},
    productsGroup: [],
    // relatedProducts: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearProduct: (state) => {
      state.product = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg.latest || action.meta.arg.params?.latest) {
          state.latestProducts = action.payload;
        } else if (action.meta.arg.popular || action.meta.arg.params?.popular) {
          state.popularProducts = action.payload;
        } else if (
          action.meta.arg.params?.bestseller ||
          action.meta.arg.params?.bestseller
        ) {
          state.bestSellerProducts = action.payload;
        } else if (action.meta.arg.params?.group) {
          state.productsGroup = action.payload;
        } else {
          state.products = action.payload;
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchLatestProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLatestProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.latestProducts = action.payload;
      })
      .addCase(fetchLatestProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchPopularProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPopularProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.popularProducts = action.payload;
      })
      .addCase(fetchPopularProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.product = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchProductsByGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsByGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.productsByGroup[action.meta.arg] = action.payload; // Store by group ID
      })

      .addCase(fetchProductsByGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSearchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchSearchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // .addCase(fetchRelatedProducts.pending, (state) => {
    //   state.loading = true;
    //   state.error = null;
    // })
    // .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
    //   state.loading = false;
    //   state.relatedProducts = action.payload.data;
    // })
    // .addCase(fetchRelatedProducts.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = action.payload;
    // });
  },
});
export const { clearProduct } = productSlice.actions;
export default productSlice.reducer;
